import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

// Course metadata with section counts
// Note: Section counts are maintained here since chapter data is served from the backend
// Update these when adding/removing sections from chapters
export const courses = [
  { id: 'binary-search', name: 'Binary Search', icon: '🔍', premium: false, sectionsCount: 5 },
  { id: 'arrays-and-hashing', name: 'Arrays & Hashing', icon: '📊', premium: true, sectionsCount: 6 },
  { id: 'two-pointer', name: 'Two Pointer', icon: '👆', premium: true, sectionsCount: 6 },
  { id: 'sliding-window', name: 'Sliding Window', icon: '🪟', premium: true, sectionsCount: 9 },
  { id: 'linked-list', name: 'Linked Lists', icon: '🔗', premium: true, sectionsCount: 9 },
  { id: 'tree', name: 'Trees', icon: '🌳', premium: true, sectionsCount: 9 },
  { id: 'trie', name: 'Trie', icon: '🔤', premium: true, sectionsCount: 6 },
  { id: 'heap', name: 'Heap', icon: '📚', premium: true, sectionsCount: 6 },
  { id: 'backtracking', name: 'Backtracking', icon: '🔙', premium: true, sectionsCount: 9 },
  { id: 'graph', name: 'Graph', icon: '🕸️', premium: true, sectionsCount: 12 },
  { id: 'dynamic-programming', name: 'Dynamic Programming', icon: '📈', premium: true, sectionsCount: 18 },
  { id: 'greedy', name: 'Greedy', icon: '🎯', premium: true, sectionsCount: 6 },
  { id: 'interval', name: 'Interval', icon: '📅', premium: true, sectionsCount: 9 },
  { id: 'monotonic-stack', name: 'Monotonic Stack', icon: '📏', premium: true, sectionsCount: 6 },
  { id: 'palindrome', name: 'Palindrome', icon: '🔄', premium: true, sectionsCount: 5 },
  { id: 'divide-and-conquer', name: 'Divide & Conquer', icon: '✂️', premium: true, sectionsCount: 5 },
  { id: 'prefix-sum', name: 'Prefix Sum', icon: '➕', premium: true, sectionsCount: 3 },
  { id: 'sorted-list', name: 'SortedList', icon: '📋', premium: true, sectionsCount: 2 },
  { id: 'segment-tree', name: 'Segment Tree', icon: '🌲', premium: true, sectionsCount: 3 },
  { id: 'parenthesis', name: 'Parenthesis', icon: '( )', premium: true, sectionsCount: 5 },
];

function CoursesGrid() {
  const navigate = useNavigate();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const [completedCourses] = useState(() => {
    const saved = localStorage.getItem('completedCourseChapters');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCourseClick = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 }, minHeight: 'calc(100vh - 120px)' }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp}>
          <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 820, mx: 'auto' }}>
            <Typography
              variant="overline"
              sx={{
                display: 'inline-flex',
                px: 2,
                py: 0.6,
                mb: 2,
                borderRadius: 999,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                fontWeight: 800,
              }}
            >
              Pattern library
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 850,
                fontSize: { xs: '2.8rem', md: '4.9rem' },
                lineHeight: 0.95,
                mb: 2,
              }}
            >
              Choose your next template.
            </Typography>
            <Typography
              sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: 650, mx: 'auto' }}
            >
              Master the 20+ essential coding patterns with battle-tested templates and curated problems
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={2.5}>
          {courses.map((course) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: completedCourses[course.id] ? 'rgba(21, 144, 95, 0.1)' : 'background.paper',
                    transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      borderColor: 'secondary.main',
                    },
                  }}
                >
                  <CardActionArea onClick={() => handleCourseClick(course.id)}>
                    <CardContent sx={{ p: 3, minHeight: 190, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(81, 104, 255, 0.1)',
                            border: '1px solid',
                            borderColor: 'divider',
                            fontSize: '1.35rem',
                          }}
                        >
                          {course.icon}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {completedCourses[course.id] && (
                            <CheckCircleIcon sx={{ color: '#15905f', fontSize: 20 }} />
                          )}
                          {course.premium && !hasActiveSubscription && (
                            <LockIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 800,
                          mb: 1,
                        }}
                      >
                        {course.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.9rem',
                          mt: 'auto',
                        }}
                      >
                        {course.sectionsCount} sections
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Container>
  );
}

export default CoursesGrid;
